// src/store/workspaceTabs.js

// -- Action Type Constants --
const WORKSPACE_TABS_ADD = "WORKSPACE_TABS_ADD";
const WORKSPACE_TABS_ADD_MANY = "WORKSPACE_TABS_ADD_MANY";
const WORKSPACE_TABS_REMOVE = "WORKSPACE_TABS_REMOVE";
const WORKSPACE_TABS_PIN = "WORKSPACE_TABS_PIN";
const WORKSPACE_TABS_UNPIN = "WORKSPACE_TABS_UNPIN";
const WORKSPACE_TABS_CLEAR = "WORKSPACE_TABS_CLEAR";

const WORKSPACE_TABS_STORAGE_KEY = `workspacePinnedTabs-${process.env.REACT_APP_PARTNER_NAME}`;
// const WORKSPACE_TABS_STORAGE_KEY = "workspacePinnedTabs";

// Helper: load pinned tabs from localStorage on startup
const loadPinnedTabsFromStorage = () => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(WORKSPACE_TABS_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((tab) => tab && tab.loanNumber != null)
      .map((tab) => ({
        loanNumber: tab.loanNumber,
        loanName: tab.loanName || "",
        // force pinned true on rehydration
        pinned: true,
      }));
  } catch (err) {
    // if anything goes wrong, just start empty
    return [];
  }
};

// Helper: add or update a single tab in a tabs array
const addOrUpdateTab = (tabs, incomingTab) => {
  if (!incomingTab || incomingTab.loanNumber == null) {
    return { tabs, changed: false };
  }

  const existingIndex = tabs.findIndex(
    (tab) => tab.loanNumber === incomingTab.loanNumber
  );

  // If tab already exists, update its data but keep pinned flag
  if (existingIndex !== -1) {
    const existingTab = tabs[existingIndex];
    const updatedTab = {
      ...existingTab,
      ...incomingTab,
      pinned:
        typeof incomingTab.pinned === "boolean"
          ? incomingTab.pinned
          : existingTab.pinned,
    };

    if (
      existingTab.loanName === updatedTab.loanName &&
      existingTab.pinned === updatedTab.pinned
    ) {
      return { tabs, changed: false };
    }

    const nextTabs = [...tabs];
    nextTabs[existingIndex] = updatedTab;

    return { tabs: nextTabs, changed: true };
  }

  // New tab: default pinned to false if not provided
  const newTab = {
    pinned: false,
    ...incomingTab,
  };

  return {
    tabs: [...tabs, newTab],
    changed: true,
  };
};

// -- Initial State --
const initialState = {
  // On page load, only pinned items are loaded
  tabs: loadPinnedTabsFromStorage(),
};

// -- Reducer --
export default function workspaceTabsReducer(state = initialState, action) {
  switch (action.type) {
    case WORKSPACE_TABS_ADD: {
      const { tabs: nextTabs, changed } = addOrUpdateTab(
        state.tabs,
        action.payload
      );

      if (!changed) {
        return state;
      }

      return {
        ...state,
        tabs: nextTabs,
      };
    }

    case WORKSPACE_TABS_ADD_MANY: {
      const incomingTabs = Array.isArray(action.payload) ? action.payload : [];

      if (incomingTabs.length === 0) return state;

      let changed = false;
      let nextTabs = state.tabs;

      incomingTabs.forEach((tab) => {
        const result = addOrUpdateTab(nextTabs, tab);
        if (result.changed) {
          changed = true;
          nextTabs = result.tabs;
        }
      });

      if (!changed) {
        return state;
      }

      return {
        ...state,
        tabs: nextTabs,
      };
    }

    case WORKSPACE_TABS_REMOVE: {
      const { loanNumber } = action.payload || {};
      if (loanNumber == null) return state;

      const nextTabs = state.tabs.filter(
        (tab) => tab.loanNumber !== loanNumber
      );

      if (nextTabs.length === state.tabs.length) {
        return state;
      }

      return {
        ...state,
        tabs: nextTabs,
      };
    }

    case WORKSPACE_TABS_PIN: {
      const { loanNumber } = action.payload || {};
      if (loanNumber == null) return state;

      let changed = false;

      const nextTabs = state.tabs.map((tab) => {
        if (tab.loanNumber !== loanNumber) return tab;
        if (tab.pinned === true) return tab;
        changed = true;
        return { ...tab, pinned: true };
      });

      if (!changed) return state;

      return {
        ...state,
        tabs: nextTabs,
      };
    }

    case WORKSPACE_TABS_UNPIN: {
      const { loanNumber } = action.payload || {};
      if (loanNumber == null) return state;

      let changed = false;

      const nextTabs = state.tabs.map((tab) => {
        if (tab.loanNumber !== loanNumber) return tab;
        if (tab.pinned === false) return tab;
        changed = true;
        return { ...tab, pinned: false };
      });

      if (!changed) return state;

      return {
        ...state,
        tabs: nextTabs,
      };
    }

    case WORKSPACE_TABS_CLEAR:
      return initialState;

    default:
      return state;
  }
}

// -- Action Creators --

export const workspaceTabsAdd = (tab) => ({
  type: WORKSPACE_TABS_ADD,
  payload: tab,
});

export const workspaceTabsAddMany = (tabs) => ({
  type: WORKSPACE_TABS_ADD_MANY,
  payload: tabs,
});

export const workspaceTabsRemove = (loanNumber) => ({
  type: WORKSPACE_TABS_REMOVE,
  payload: { loanNumber },
});

export const workspaceTabsPin = (loanNumber) => ({
  type: WORKSPACE_TABS_PIN,
  payload: { loanNumber },
});

export const workspaceTabsUnpin = (loanNumber) => ({
  type: WORKSPACE_TABS_UNPIN,
  payload: { loanNumber },
});

export const workspaceTabsClear = () => ({
  type: WORKSPACE_TABS_CLEAR,
});

// If you want the key in the component:
export const WORKSPACE_TABS_STORAGE = WORKSPACE_TABS_STORAGE_KEY;
